import { initializeApp } from 'firebase/app'
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from 'firebase/app-check'
import {
  getAuth,
  onAuthStateChanged,
  signOut as signOutFirebase,
  User as UserFirebase,
} from 'firebase/auth'
import { AGENT_PREVIEW_ENABLED } from './agentPreview/config'
import { AGENT_PREVIEW_USER } from './agentPreview/identity'
import { authSlice } from './redux/slices'
import { dispatch } from './redux/store'

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
}

const isProduction = process.env.NODE_ENV === 'production'
const app = initializeApp(firebaseConfig)

if (!isProduction) {
  /*
   Enable debug token for Firebase App Check
   Check: https://firebase.google.com/docs/app-check/web/debug-provider#localhost
   You will need to open the console and copy the token from there to Firebase App check console.
   Open production, beast web, and in the burger menu, click on "Manage debug tokens"
   More details here: https://github.com/electricbeast/beast-app/pull/6782
  */

  // @ts-expect-error Firebase App Check debug token
  self.FIREBASE_APPCHECK_DEBUG_TOKEN = false
}

// App Check fetches a reCAPTCHA Enterprise challenge from Google, which no
// unattended browser can answer and which needs a site key the preview does not
// have. The preview never reaches a real backend, so there is nothing to attest.
if (!AGENT_PREVIEW_ENABLED) {
  initializeAppCheck(app, {
    provider: new ReCaptchaEnterpriseProvider(
      import.meta.env.VITE_RECAPTCHA_SITE_KEY,
    ),
    isTokenAutoRefreshEnabled: true, // Set to true to allow auto-refresh.
  })
}

const auth = getAuth(app)

auth.useDeviceLanguage()
auth.settings.appVerificationDisabledForTesting = !isProduction

/**
 * The signed-in user the preview reports. Only the fields the application reads
 * are populated — `getIdToken` for the Apollo authorization header, and the
 * identity fields the profile menu shows.
 */
const agentPreviewUser = {
  uid: AGENT_PREVIEW_USER.uid,
  email: AGENT_PREVIEW_USER.email,
  displayName: AGENT_PREVIEW_USER.displayName,
  emailVerified: true,
  isAnonymous: false,
  providerData: [],
  getIdToken: async () => AGENT_PREVIEW_USER.idToken,
  // The Firebase `User` interface carries ~20 members the backoffice never
  // touches; standing them all up would be noise, so the shape above is asserted
  // to it. Nothing outside preview mode can observe this value.
} as unknown as UserFirebase

/**
 * Preview mode's stand-in for Firebase's own auth state: who is signed in, and
 * who to tell when that changes. Firebase keeps both internally; the preview has
 * to keep them itself, or signing out leaves every subscriber believing the
 * session is still live.
 */
const previewAuth = {
  user: agentPreviewUser as UserFirebase | null,
  subscribers: new Set<(user: UserFirebase | null) => void>(),

  set(user: UserFirebase | null) {
    this.user = user
    this.subscribers.forEach((notify) => {
      notify(user)
    })
  },
}

const getFirebaseUser = (): UserFirebase | null => {
  if (AGENT_PREVIEW_ENABLED) {
    return previewAuth.user
  }

  return auth.currentUser
}

/**
 * Subscribes to sign-in state, returning the unsubscribe function.
 *
 * Wrapping `onAuthStateChanged` gives preview mode one place to report a
 * signed-in user without a real identity provider, and keeps `AuthProvider`
 * unaware that there is more than one way to be signed in.
 */
const observeAuthState = (
  onChange: (user: UserFirebase | null) => void,
): (() => void) => {
  if (AGENT_PREVIEW_ENABLED) {
    previewAuth.subscribers.add(onChange)
    onChange(previewAuth.user)

    return () => {
      previewAuth.subscribers.delete(onChange)
    }
  }

  return onAuthStateChanged(auth, onChange)
}

const signOut = async (): Promise<void> => {
  dispatch(authSlice.actions.signOut())

  if (AGENT_PREVIEW_ENABLED) {
    // `AppLayout` renders nothing until auth state is determinate, and in
    // preview nothing else will ever make it so. Reporting the sign-out is what
    // lets `AuthProvider` redirect to /login instead of leaving a white page.
    previewAuth.set(null)
    return undefined
  }

  return signOutFirebase(auth)
}

export { app, auth, getFirebaseUser, observeAuthState, signOut }
